import React from 'react'
import { Link, Navigate } from 'react-router-dom'
import { useAppTranslation } from '../../../hooks/useAppTranslation'
import BoappaLogo from '../../../assets/icons/boappa.svg'
import { theme } from '../../../theme/theme'
import { SignUpForm } from './SignUpForm'
import { LanguagePicker } from '../../../components/authentication/LanguagePicker'
import { reverseUrl } from '../../../navigation/reverseUrl'
import { useDocumentTitle } from '../../../hooks/useDocumentTitle'
import { reverseDocumentTitle } from '../../../navigation/reverseDocumentTitle'
import { useAuth } from '../../../hooks/useAuth'
import { i18nSocietyType } from '../../../i18n'

export const SignUpView = (): JSX.Element => {
  const { userId } = useAuth()
  const { translate } = useAppTranslation()
  useDocumentTitle(reverseDocumentTitle('signup'))

  // Somebody already signed in has nothing to sign up for. This used to send
  // them to the verify-email page, which passed them on to the feed once it had
  // polled the account and found it verified; that page no longer polls, and a
  // signed-in account is a verified one, so send them there directly.
  //
  // Redirected during render rather than from an effect, and replacing rather
  // than pushing, which is how LoginRoute and AuthenticatedRoute already do it.
  // An effect runs after the form has been painted, so it flashed past first;
  // and a push left /signup sitting in the back stack, so stepping back landed
  // on a page that immediately threw you forward again -- a back button that
  // looks broken.
  if (userId) {
    return <Navigate to={reverseUrl('feed')} replace />
  }

  return (
    <div className="flex h-full flex-col overflow-y-auto ">
      <div className="flex p-4 md:p-8 justify-between items-center">
        <BoappaLogo
          className="w-16 h-[14px] md:w-40 md:h-auto "
          alt="boappa logo"
        />
        <LanguagePicker />
      </div>
      <div className="flex flex-1 flex-col items-center pb-4 md:pb-8">
        <div className="flex w-[90%] flex-1 flex-col justify-center md:w-[80%] xl:w-[50%]">
          <div className="flex flex-col gap-4">
            <p style={theme.textVariants.h1}>
              {translate('authenticationSignUpView.title')}
            </p>
            <p style={theme.textVariants.paragraph}>
              {translate('authenticationSignUpView.subTitle', {
                context: i18nSocietyType(undefined),
              })}
            </p>
            <SignUpForm />
            <p
              className="mt-4 mb-4 flex justify-center gap-2 md:mb-0"
              style={theme.textVariants.paragraph}
            >
              {translate('authenticationSignUpView.loginButtonTitle')}
              <Link
                className="underline hover:text-blue"
                to={reverseUrl('login')}
              >
                {translate('authenticationSignUpView.loginCTAButtonTitle')}
              </Link>
            </p>
            <p
              className="mb-4 flex justify-center md:mb-0"
              style={theme.textVariants.paragraph}
            >
              <Link
                className="underline hover:text-blue"
                to={reverseUrl('reset-password')}
              >
                {translate(
                  'authenticationSignUpView.forgotPasswordButtonTitle'
                )}
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}
